import {
  calculateTrackDistance,
  getTrackElevationGain,
} from '@/services/trackMetricsCalculationService'
import { renderWaypointIconHtml } from '@/services/waypointIconService'
import type { Tour, Track, Waypoint } from '@/types/tour'
import { getCreatorByline } from '@/utils/creatorByline'
import { getEffectiveTourName } from '@/utils/tourName'
import { escapeHtml } from '@/utils/trackHoverPopup'

const POPUP_ROUTE_ICON_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" ` +
  `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
  `style="color:#111827;flex-shrink:0;" aria-hidden="true">` +
  `<circle cx="6" cy="19" r="3"/>` +
  `<path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/>` +
  `<circle cx="18" cy="5" r="3"/>` +
  `</svg>`

const POPUP_RULER_ICON_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" ` +
  `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
  `style="color:#111827;flex-shrink:0;" aria-hidden="true">` +
  `<path d="M21.3 8.7 8.7 21.3c-1 1-2.5 1-3.4 0l-2.6-2.6c-1-1-1-2.5 0-3.4L15.3 2.7c1-1 2.5-1 3.4 0l2.6 2.6c1 1 1 2.5 0 3.4Z"/>` +
  `<path d="m7.5 10.5 3 3"/><path d="m10.5 7.5 3 3"/><path d="m13.5 4.5 3 3"/><path d="m4.5 13.5 3 3"/>` +
  `</svg>`

const POPUP_ELEV_ICON_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" ` +
  `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
  `style="color:#111827;flex-shrink:0;" aria-hidden="true">` +
  `<path d="M7 17 17 7"/><path d="M7 7h10v10"/>` +
  `</svg>`

const POPUP_WAYPOINT_COUNT_ICON_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" ` +
  `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
  `style="color:#111827;flex-shrink:0;" aria-hidden="true">` +
  `<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>` +
  `</svg>`

const chip = (icon: string, value: string) =>
  `<span style="display:flex;align-items:center;gap:4px;">${icon}<span style="color:#111827;">${value}</span></span>`

const row = (items: string[], marginTop = '2px') =>
  `<div class="text-xs" style="display:flex;align-items:center;gap:8px;margin-top:${marginTop};">${items.join('')}</div>`

const formatDistance = (distanceM: number) =>
  distanceM >= 1000 ? `${(distanceM / 1000).toFixed(1)} km` : `${Math.round(distanceM)} m`

export function buildTourHoverPopupHtml(tour: Tour): string {
  const rawName = tour.name?.trim() || 'Untitled Tour'
  const truncatedName = rawName.length > 24 ? rawName.slice(0, 24) + '…' : rawName
  const safeName = escapeHtml(truncatedName)
  const trackCount = tour.tracks.length
  const waypointCount = tour.waypoints.length

  let totalDistanceM = 0
  let totalElevationGainM: number | null = null
  let earliestStartTime: number | null = null
  for (const track of tour.tracks) {
    totalDistanceM += calculateTrackDistance(track)
    const gain = getTrackElevationGain(track)
    if (gain !== null) totalElevationGainM = (totalElevationGainM ?? 0) + gain
    if (
      track.startTime != null &&
      (earliestStartTime === null || track.startTime < earliestStartTime)
    ) {
      earliestStartTime = track.startTime
    }
  }

  const distanceStr =
    totalDistanceM > 0
      ? totalDistanceM >= 1000
        ? `${(totalDistanceM / 1000).toFixed(1)} km`
        : `${Math.round(totalDistanceM)} m`
      : null
  const elevationStr =
    totalElevationGainM !== null && totalElevationGainM > 0
      ? `+${Math.round(totalElevationGainM)} m`
      : null
  const dateStr =
    earliestStartTime !== null
      ? new Date(earliestStartTime).toLocaleDateString(undefined, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : null

  // Line 2: date only
  const dateRow = dateStr ? row([`<span style="color:#111827;">${escapeHtml(dateStr)}</span>`]) : ''

  // Line 3: waypoint count, track count, distance, elevation
  const statsItems: string[] = []
  if (trackCount > 1) statsItems.push(chip(POPUP_ROUTE_ICON_SVG, String(trackCount)))
  if (waypointCount > 0) {
    statsItems.push(chip(POPUP_WAYPOINT_COUNT_ICON_SVG, String(waypointCount)))
  }
  if (distanceStr) statsItems.push(chip(POPUP_RULER_ICON_SVG, escapeHtml(distanceStr)))
  if (elevationStr) statsItems.push(chip(POPUP_ELEV_ICON_SVG, escapeHtml(elevationStr)))
  const statsRow = statsItems.length > 0 ? row(statsItems) : ''

  const nameRow = `<div class="text-sm font-semibold" style="max-width:260px;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${safeName}</div>`
  return nameRow + dateRow + statsRow
}

const truncatePopupText = (value: string, maxLength = 24) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}…` : value

const resolvePopupLabel = (value: string | null | undefined, fallback: string) => {
  const trimmed = value?.trim() || fallback
  return escapeHtml(truncatePopupText(trimmed))
}

const buildPrimaryTextRow = (content: string, byline: string | null = null) =>
  `<div class="text-sm font-semibold" style="max-width:260px;color:#111827;display:flex;align-items:baseline;gap:4px;white-space:nowrap;">` +
  `<span style="min-width:0;overflow:hidden;text-overflow:ellipsis;">${content}</span>` +
  (byline
    ? `<span style="flex-shrink:0;color:#6b7280;font-size:12px;font-weight:400;">${escapeHtml(byline)}</span>`
    : '') +
  `</div>`

const buildSecondaryTextRow = (content: string) =>
  `<div class="text-sm" style="max-width:260px;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:2px;">${content}</div>`

const buildWaypointLabelRow = (content: string) =>
  `<div class="text-sm" style="max-width:260px;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:2px;display:flex;align-items:center;gap:6px;">${content}</div>`

export function buildWorkspaceTrackHoverPopupHtml(
  tour: Tour,
  track: Track,
  trackIndex: number,
): string {
  const rows: string[] = [
    buildPrimaryTextRow(
      resolvePopupLabel(getEffectiveTourName(tour), 'Untitled Tour'),
      getCreatorByline(tour),
    ),
  ]

  if (tour.tracks.length > 1) {
    rows.push(buildSecondaryTextRow(resolvePopupLabel(track.name, `Track ${trackIndex + 1}`)))
  }

  const statsItems: string[] = []
  const distanceM = calculateTrackDistance(track)
  const elevationGainM = getTrackElevationGain(track)

  if (distanceM > 0) {
    statsItems.push(chip(POPUP_RULER_ICON_SVG, escapeHtml(formatDistance(distanceM))))
  }
  if (elevationGainM !== null && elevationGainM > 0) {
    statsItems.push(chip(POPUP_ELEV_ICON_SVG, `+${Math.round(elevationGainM)} m`))
  }

  if (statsItems.length > 0) {
    rows.push(row(statsItems))
  }

  return rows.join('')
}

export function buildWorkspaceWaypointHoverPopupHtml(tour: Tour, waypoint: Waypoint): string {
  return (
    buildPrimaryTextRow(
      resolvePopupLabel(getEffectiveTourName(tour), 'Untitled Tour'),
      getCreatorByline(tour),
    ) +
    buildWaypointLabelRow(
      `<span class="shrink-0" style="display:inline-flex;align-items:center;">${renderWaypointIconHtml(
        waypoint.type,
        { size: 16 },
      )}</span><span>${resolvePopupLabel(waypoint.name, 'Waypoint')}</span>`,
    )
  )
}
